/**
 * Workflow step HTML/URL sanitization (DOMPurify).
 *
 * Two image URL policies:
 * - persist: save payloads and editor state (https + maintella-media:// only)
 * - display: runtime render after API ResolveMediaReferences (also allows http presigns in dev)
 *
 * Pipeline:
 * - Stored JSON: maintella-media:// refs, graphic_media_id
 * - Admin GET: workflow_steps (stored) + resolved_workflow_steps (display)
 * - Frontend persist on save (workflowSanitize); display at render (WorkflowStepInstruction)
 * - Backend Workflows::HtmlSanitizer on save
 */
import DOMPurify from 'dompurify';
import { isMaintellaMediaUrl } from './mediaLibraryUrls';

const ALLOWED_TAGS = [
  'p', 'br', 'strong', 'em', 'b', 'i', 'u', 's', 'a', 'img',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li', 'blockquote', 'pre', 'code', 'span', 'div',
  'figure', 'figcaption',
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
];

const ALLOWED_ATTR = ['href', 'src', 'alt', 'title', 'target', 'rel', 'class'];

const ALLOWED_URI_REGEXP =
  /^(?:(?:(?:f|ht)tps?|maintella-media):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i;

export const IMAGE_URL_POLICY = {
  PERSIST: 'persist',
  DISPLAY: 'display',
};

let hooksInstalled = false;
let activeImageUrlPolicy = IMAGE_URL_POLICY.PERSIST;

function isAllowedDisplayImageUrl(url) {
  if (!url) return false;
  if (isMaintellaMediaUrl(url)) return true;

  try {
    const parsed = new URL(url.trim());
    return (parsed.protocol === 'https:' || parsed.protocol === 'http:') && Boolean(parsed.host);
  } catch {
    return false;
  }
}

function isAllowedImageUrlForPolicy(url, policy) {
  return policy === IMAGE_URL_POLICY.DISPLAY
    ? isAllowedDisplayImageUrl(url)
    : isAllowedWorkflowUrl(url);
}

function installHooks() {
  if (hooksInstalled || typeof window === 'undefined') return;

  DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
    if (data.attrName !== 'src' && data.attrName !== 'href') return;

    const tag = node.tagName;
    if (data.attrName === 'src' && tag === 'IMG' && isMaintellaMediaUrl(data.attrValue)) {
      data.keepAttr = true;
      return;
    }
    if (data.attrName === 'src' && tag === 'IMG') {
      if (!isAllowedImageUrlForPolicy(data.attrValue, activeImageUrlPolicy)) {
        data.keepAttr = false;
      }
      return;
    }
    if (data.attrName === 'href' && tag === 'A' && !isAllowedWorkflowUrl(data.attrValue)) {
      data.keepAttr = false;
    }
  });

  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName !== 'A' || node.getAttribute('target') !== '_blank') return;

    const rel = new Set((node.getAttribute('rel') || '').split(/\s+/).filter(Boolean));
    rel.add('noopener');
    rel.add('noreferrer');
    node.setAttribute('rel', [...rel].join(' '));
  });

  hooksInstalled = true;
}

function sanitizeWorkflowHtmlInternal(html, imageUrlPolicy) {
  if (!html) return '';

  installHooks();
  const previousPolicy = activeImageUrlPolicy;
  activeImageUrlPolicy = imageUrlPolicy;
  try {
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS,
      ALLOWED_ATTR,
      ALLOWED_URI_REGEXP,
    });
  } finally {
    activeImageUrlPolicy = previousPolicy;
  }
}

export function isAllowedWorkflowUrl(url) {
  if (!url) return false;
  if (isMaintellaMediaUrl(url)) return true;

  try {
    const parsed = new URL(url.trim());
    return parsed.protocol === 'https:' && Boolean(parsed.host);
  } catch {
    return false;
  }
}

export function sanitizeWorkflowUrlForPersist(url) {
  if (!url || typeof url !== 'string') return '';
  return isAllowedWorkflowUrl(url) ? url.trim() : '';
}

export function sanitizeWorkflowUrlForDisplay(url) {
  if (!url || typeof url !== 'string') return '';
  return isAllowedDisplayImageUrl(url) ? url.trim() : '';
}

export function sanitizeWorkflowHtmlForPersist(html) {
  return sanitizeWorkflowHtmlInternal(html, IMAGE_URL_POLICY.PERSIST);
}

export function sanitizeWorkflowHtmlForDisplay(html) {
  return sanitizeWorkflowHtmlInternal(html, IMAGE_URL_POLICY.DISPLAY);
}
